import * as React from "react";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/**
 * SearchableCombobox - A searchable dropdown with create new option
 * 
 * Props:
 * - options: Array of {value, label, group?} objects
 * - value: Currently selected value
 * - onValueChange: (value) => void
 * - placeholder: Placeholder text
 * - searchPlaceholder: Search input placeholder
 * - emptyText: Text when no results
 * - allowCreate: Show "+ Create New" option
 * - onCreateNew: () => void - Called when create new is clicked
 * - createNewText: Text for create new option
 * - grouped: Boolean - if true, groups items by 'group' field
 * - disabled: Boolean
 * - className: Additional classes
 */
export function SearchableCombobox({
  options = [],
  value,
  onValueChange,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyText = "No results found.",
  allowCreate = false,
  onCreateNew,
  createNewText = "+ Create New",
  grouped = false,
  disabled = false,
  className,
  'data-testid': testId,
}) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");

  // Find selected option label
  const selectedOption = options.find((opt) => opt.value === value);
  const displayValue = selectedOption?.label || "";

  // Filter options based on search
  const filteredOptions = React.useMemo(() => {
    if (!searchQuery) return options;
    const query = searchQuery.toLowerCase();
    return options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(query) ||
        (opt.group && opt.group.toLowerCase().includes(query))
    );
  }, [options, searchQuery]);

  // Group options if grouped prop is true
  const groupedOptions = React.useMemo(() => {
    if (!grouped) return { ungrouped: filteredOptions };
    
    // Sort groups alphabetically and preserve order within groups
    const groups = {};
    filteredOptions.forEach(opt => {
      const group = opt.group || "Other";
      if (!groups[group]) groups[group] = [];
      groups[group].push(opt);
    });
    
    // Sort group keys
    const sortedGroups = {};
    Object.keys(groups).sort().forEach(key => {
      sortedGroups[key] = groups[key];
    });
    
    return sortedGroups;
  }, [filteredOptions, grouped]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-testid={testId}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "w-full justify-between font-normal bg-white text-stone-800 border-stone-300 hover:bg-stone-50",
            !value && "text-stone-500",
            className
          )}
        >
          <span className="truncate">
            {displayValue || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false} className="bg-white">
          <CommandInput
            placeholder={searchPlaceholder}
            value={searchQuery}
            onValueChange={setSearchQuery}
            className="text-stone-800"
          />
          <CommandList className="max-h-[350px] overflow-y-auto scrollbar-visible">
            <CommandEmpty className="text-stone-500">{emptyText}</CommandEmpty>
            
            {/* Create New Option — auto-enabled when onCreateNew is provided
                (iter-381), so callers don't need to also pass `allowCreate`. */}
            {(allowCreate || onCreateNew) && (
              <>
                <CommandGroup>
                  <CommandItem
                    onSelect={() => {
                      onCreateNew?.();
                      setOpen(false);
                      setSearchQuery("");
                    }}
                    className="text-blue-600 cursor-pointer font-medium"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {createNewText}
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator />
              </>
            )}

            {/* Options */}
            {grouped ? (
              Object.entries(groupedOptions).map(([group, items]) => (
                <CommandGroup 
                  key={group} 
                  heading={
                    <span className="text-xs font-bold text-stone-600 uppercase tracking-wide bg-stone-100 px-2 py-1 rounded">
                      {group}
                    </span>
                  }
                  className="pb-2"
                >
                  {items.map((option) => (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      onSelect={() => {
                        onValueChange(option.value === value ? "" : option.value);
                        setOpen(false);
                        setSearchQuery("");
                      }}
                      className="cursor-pointer text-stone-700 hover:bg-stone-100"
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4 text-stone-800",
                          value === option.value ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <span className="text-stone-800">{option.label}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))
            ) : (
              <CommandGroup>
                {filteredOptions.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => {
                      onValueChange(option.value === value ? "" : option.value);
                      setOpen(false);
                      setSearchQuery("");
                    }}
                    className="cursor-pointer text-stone-700 hover:bg-stone-100"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4 text-stone-800",
                        value === option.value ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <span className="text-stone-800">{option.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default SearchableCombobox;
